import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useEffect, useRef } from "react";

import { useShouldShowFortuneCookie } from "../../../FortuneCookie/hooks/useShouldShowFortuneCookie";
import type { OpenShiftListItem } from "../../../Shift/Open/types";
import { createShiftSignature } from "./utils";

const FIXED_POSITION = 4;

interface ShouldSkipFortuneCookieInjectionParams {
  shouldShow: boolean;
  shiftsCount: number;
  fixedPosition: number;
}

function shouldSkipFortuneCookieInjection(params: ShouldSkipFortuneCookieInjectionParams): boolean {
  const { shouldShow, shiftsCount } = params;
  return !shouldShow || shiftsCount === 0;
}

function logFortuneCookieInjection(metadata?: Record<string, any>): void {
  logEvent(APP_V2_APP_EVENTS.FORTUNE_COOKIE_INJECTED_INTO_SHIFTS, {
    timestamp: new Date().toISOString(),
    ...metadata,
  });
}

function injectFortuneCookie(shifts: OpenShiftListItem[], position: number): OpenShiftListItem[] {
  // Get the start time from the shift at the injection position for grouping
  const shiftAtPosition = shifts[position - 1];
  const startTime =
    shiftAtPosition?.type === "open-shift"
      ? (shiftAtPosition.attributes?.start ?? "")
      : (shiftAtPosition?.start ?? "");

  // Insert fortune cookie after the specified position
  const result: OpenShiftListItem[] = [...shifts];
  result.splice(position, 0, {
    type: "fortune-cookie",
    start: startTime,
  } satisfies OpenShiftListItem);

  logFortuneCookieInjection({
    shift_position: position,
    shifts_count: shifts.length,
  });

  return result;
}

/**
 * Injects a fortune cookie card into the shift list.
 * - If there are 4+ shifts: after the 4th shift
 * - If there are fewer shifts: after the last shift
 */
export function useFortuneCookieInjector(shifts: OpenShiftListItem[]): OpenShiftListItem[] {
  const { shouldShow, markAsShowing } = useShouldShowFortuneCookie();

  // Cache shifts and fortune cookie signatures to ensure fortune cookies are appropriately sticky, but not too
  // sticky. I.E. once rendered they stay, but will not be re-rendered if the shifts change.
  const lastResultWithFortuneCookieRef = useRef<OpenShiftListItem[] | undefined>(undefined);
  const lastShiftSignatureRef = useRef<string>("");

  const currentShiftSignature = createShiftSignature(shifts);

  // Check if we should skip fortune cookie injection
  const shouldSkip = shouldSkipFortuneCookieInjection({
    shouldShow,
    shiftsCount: shifts.length,
    fixedPosition: FIXED_POSITION,
  });

  if (shouldSkip) {
    lastResultWithFortuneCookieRef.current = undefined;
    lastShiftSignatureRef.current = "";
  }

  // Check if we're using cached result
  const isCachedResult =
    !shouldSkip &&
    lastShiftSignatureRef.current === currentShiftSignature &&
    lastResultWithFortuneCookieRef.current !== undefined;

  // Determine if we need to inject a new fortune cookie
  const shouldInjectNew = !shouldSkip && !isCachedResult;

  // Mark as showing after injection (in useEffect to avoid state update during render)
  useEffect(() => {
    if (shouldInjectNew) {
      markAsShowing();
    }
  }, [shouldInjectNew, markAsShowing]);

  // Early return if skipping
  if (shouldSkip) {
    return shifts;
  }

  // Return cached result if available
  if (isCachedResult) {
    return lastResultWithFortuneCookieRef.current!;
  }

  // Inject fortune cookie at fixed position or after last shift if fewer shifts available
  const injectionPosition = Math.min(FIXED_POSITION, shifts.length);
  const result = injectFortuneCookie(shifts, injectionPosition);

  // Cache the result and signature
  lastResultWithFortuneCookieRef.current = result;
  lastShiftSignatureRef.current = currentShiftSignature;

  return result;
}
